import React from 'react';
import { X, AlertTriangle, CheckCircle, Trash2, Info, Loader2 } from 'lucide-react';

interface DocumentActionModalProps {
    isOpen: boolean;
    type: 'success' | 'error' | 'delete' | 'confirm';
    title: string;
    message: string;
    onClose: () => void;
    onConfirm?: () => void;
    isLoading?: boolean;
}

const DocumentActionModal: React.FC<DocumentActionModalProps> = ({
    isOpen,
    type,
    title,
    message,
    onClose,
    onConfirm,
    isLoading = false
}) => {
    if (!isOpen) return null;

    // Determine config based on type
    const getConfig = () => {
        switch (type) {
            case 'success':
                return {
                    icon: CheckCircle,
                    color: 'text-green-600',
                    bgColor: 'bg-green-100',
                    borderColor: 'border-green-100',
                    btnColor: 'bg-green-600 hover:bg-green-700',
                    showConfirm: false,
                    closeLabel: 'Close'
                };
            case 'error':
                return {
                    icon: AlertTriangle,
                    color: 'text-red-600',
                    bgColor: 'bg-red-100',
                    borderColor: 'border-red-100',
                    btnColor: 'bg-red-600 hover:bg-red-700',
                    showConfirm: false,
                    closeLabel: 'Close'
                };
            case 'delete':
                return {
                    icon: Trash2,
                    color: 'text-red-600',
                    bgColor: 'bg-red-100',
                    borderColor: 'border-red-100',
                    btnColor: 'bg-red-600 hover:bg-red-700',
                    showConfirm: true,
                    confirmLabel: 'Delete',
                    closeLabel: 'Cancel'
                };
            case 'confirm':
            default:
                return {
                    icon: Info,
                    color: 'text-blue-600',
                    bgColor: 'bg-blue-100',
                    borderColor: 'border-blue-100',
                    btnColor: 'bg-blue-600 hover:bg-blue-700',
                    showConfirm: true,
                    confirmLabel: 'Confirm',
                    closeLabel: 'Cancel'
                };
        }
    };

    const config = getConfig();
    const Icon = config.icon;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl transform transition-all scale-100">
                <div className="p-6 text-center">
                    <div className={`w-16 h-16 ${config.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                        <Icon size={32} className={config.color} />
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                        {title}
                    </h3>

                    <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                        {message}
                    </p>

                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className={`px-5 py-2.5 rounded-xl font-bold transition text-slate-600 hover:bg-slate-100 ${!config.showConfirm ? 'w-full bg-slate-100' : ''}`}
                        >
                            {config.closeLabel}
                        </button>

                        {config.showConfirm && onConfirm && (
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className={`px-6 py-2.5 rounded-xl font-bold text-white transition flex items-center gap-2 ${config.btnColor} disabled:opacity-50`}
                            >
                                {isLoading && <Loader2 className="animate-spin" size={18} />}
                                {config.confirmLabel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentActionModal;
